import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LoadingOutlined, LockOutlined, PlusOutlined, RocketOutlined } from '@ant-design/icons';
import {
  DeptConf, SCENES, AGENT_ICONS, DeptCategory, PERSONAS,
  HALL_USER_BG, HALL_SLOTS, HALL_PAGES, HALL_FLOORS, HallEntry,
} from '../office-config';
import { OfficeAgent, OfficeTask } from '../useOfficeEngine';
import { Beat, TheaterState, beatBubble } from '../useTheater';
import { SpeechBubble, deptOf } from '../shared';

/* ============================================================
   沙盘展厅 · 用户素材版（玻璃树脂工作台 + 深蓝地面）
   双页（业主/外协）各 5 个弧形槽位；台子=数字员工坐姿办公的
   玻璃工作台（透明底直贴地面）；不足的槽位=灰色"待接入"台。
   点击态/办理态 = 基于透明通道的软件光效（提亮/辉光/呼吸），
   全部从 alpha 生成，边缘干净。
   ============================================================ */

/* ---------------- 令牌/回执飞行 canvas ---------------- */
interface Comet { sx: number; sy: number; tx: number; ty: number; t0: number; dur: number; color: string; token?: boolean }

function useFxCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const cometsRef = useRef<Comet[]>([]);
  const runningRef = useRef(false);

  const pump = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const step = () => {
      const now = performance.now();
      cometsRef.current = cometsRef.current.filter((c) => now - c.t0 < c.dur + 400);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!cometsRef.current.length) { runningRef.current = false; return; }
      for (const c of cometsRef.current) {
        const k = Math.min(1, (now - c.t0) / c.dur);
        const ease = 1 - Math.pow(1 - k, 3);
        const mx = (c.sx + c.tx) / 2;
        const my = Math.min(c.sy, c.ty) - Math.abs(c.tx - c.sx) * 0.18 - 90;
        const at = (tt: number) => {
          const a = 1 - tt;
          return {
            x: a * a * c.sx + 2 * a * tt * mx + tt * tt * c.tx,
            y: a * a * c.sy + 2 * a * tt * my + tt * tt * c.ty,
          };
        };
        for (let i = 0; i < 9; i++) {
          const tt = Math.max(0, ease - i * 0.035);
          const p = at(tt);
          ctx.beginPath();
          ctx.arc(p.x, p.y, Math.max(0.5, 7 - i * 0.7), 0, Math.PI * 2);
          ctx.fillStyle = c.color;
          ctx.globalAlpha = Math.max(0, 0.85 - i * 0.09) * (k < 1 ? 1 : Math.max(0, 1 - (now - c.t0 - c.dur) / 400));
          ctx.shadowColor = c.color;
          ctx.shadowBlur = 18 - i;
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
        if (c.token && k < 1) {
          const hp = at(ease);
          ctx.save();
          ctx.beginPath();
          ctx.arc(hp.x, hp.y, 13, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(12,16,30,.92)';
          ctx.strokeStyle = c.color;
          ctx.lineWidth = 2;
          ctx.shadowColor = c.color;
          ctx.shadowBlur = 22;
          ctx.fill();
          ctx.stroke();
          ctx.shadowBlur = 0;
          ctx.fillStyle = c.color;
          ctx.font = 'bold 13px "Noto Sans SC", sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('令', hp.x, hp.y + 1);
          ctx.restore();
        }
        if (k >= 1) {
          const w = (now - c.t0 - c.dur) / 400;
          ctx.beginPath();
          ctx.arc(c.tx, c.ty, 12 + w * 46, 0, Math.PI * 2);
          ctx.strokeStyle = c.color;
          ctx.globalAlpha = Math.max(0, 0.7 - w * 0.7);
          ctx.lineWidth = 2.4;
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
      }
      requestAnimationFrame(step);
    };
    if (!runningRef.current) { runningRef.current = true; requestAnimationFrame(step); }
  }, []);

  const fire = useCallback((sx: number, sy: number, tx: number, ty: number, color: string, token = false) => {
    const canvas = canvasRef.current;
    if (canvas && (canvas.width !== window.innerWidth || canvas.height !== window.innerHeight)) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    cometsRef.current.push({ sx, sy, tx, ty, t0: performance.now(), dur: token ? 1150 : 950, color, token });
    pump();
  }, [pump]);

  return { canvasRef, fire };
}

/* ---------------- 部门 → 中央舞台 流光线 ---------------- */
interface Beam { x0: number; y0: number; x1: number; y1: number; color: string; born: number }

function useBeamCanvas(beamRef: React.MutableRefObject<Beam | null>) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    let alive = true;
    const fit = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    fit();
    window.addEventListener('resize', fit);
    const step = (now: number) => {
      if (!alive) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const b = beamRef.current;
      if (b) {
        const grow = Math.min(1, (now - b.born) / 520);
        const ease = 1 - Math.pow(1 - grow, 3);
        const mx = (b.x0 + b.x1) / 2, my = (b.y0 + b.y1) / 2;
        const dx = b.x1 - b.x0, dy = b.y1 - b.y0;
        const len = Math.max(1, Math.hypot(dx, dy));
        const sign = b.x0 < b.x1 ? 1 : -1;
        const cx2 = mx + (-dy / len) * len * 0.1 * sign;
        const cy2 = my + (dx / len) * len * 0.1 * sign;
        const at = (t: number) => {
          const a = 1 - t;
          return { x: a * a * b.x0 + 2 * a * t * cx2 + t * t * b.x1, y: a * a * b.y0 + 2 * a * t * cy2 + t * t * b.y1 };
        };
        const trace = () => {
          ctx.beginPath();
          for (let i = 0; i <= 46; i++) {
            const pt = at((i / 46) * ease);
            if (i === 0) ctx.moveTo(pt.x, pt.y); else ctx.lineTo(pt.x, pt.y);
          }
        };
        ctx.save();
        ctx.lineCap = 'round';
        /* v16 流光线加重：宽光晕 + 亮主干 + 白色芯线 */
        trace(); ctx.strokeStyle = b.color; ctx.globalAlpha = 0.16; ctx.lineWidth = 24; ctx.stroke();
        trace(); ctx.globalAlpha = 0.36; ctx.lineWidth = 10; ctx.stroke();
        trace(); ctx.globalAlpha = 0.95; ctx.lineWidth = 3.4; ctx.shadowColor = b.color; ctx.shadowBlur = 20; ctx.stroke();
        trace(); ctx.strokeStyle = '#ffffff'; ctx.globalAlpha = 0.55; ctx.lineWidth = 1.3; ctx.shadowBlur = 8; ctx.stroke();
        ctx.shadowBlur = 0;
        if (ease >= 1) {
          for (let i = 0; i < 13; i++) {
            const speed = 1900 + i * 380;
            const t = ((now + i * 540) % speed) / speed;
            const pt = at(t);
            const fade = Math.min(1, t / 0.12, (1 - t) / 0.12);
            const r = 2.2 + (i % 4) * 1.05;
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2);
            ctx.fillStyle = i % 2 ? '#ffffff' : b.color;
            ctx.globalAlpha = 0.95 * fade;
            ctx.shadowColor = b.color;
            ctx.shadowBlur = 18;
            ctx.fill();
            /* 拖尾小点 */
            const pt2 = at(Math.max(0, t - 0.03));
            ctx.beginPath();
            ctx.arc(pt2.x, pt2.y, r * 0.55, 0, Math.PI * 2);
            ctx.globalAlpha = 0.4 * fade;
            ctx.fill();
          }
        }
        ctx.restore();
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    return () => { alive = false; window.removeEventListener('resize', fit); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return canvasRef;
}

/* ---------------- 模式主组件 ---------------- */
export interface HallModeProps {
  scene: DeptCategory;
  setScene: (c: DeptCategory) => void;
  deptAgents: Record<string, OfficeAgent[]>;
  tasks: OfficeTask[];
  theater: TheaterState;
  runningByDept: Record<string, OfficeTask | undefined>;
  selDeptId: string | null;
  selAgentId: string | null;
  pingDeptId: string | null;
  onChooseDept: (id: string) => void;
  onPickAgent: (a: OfficeAgent) => void;
  onOpenTask: (localId: string) => void;
  /** 运镜聚焦的部门（下达任务后镜头推进） */
  focusDeptId?: string | null;
  onExitFocus?: () => void;
}

/** 上浮光尘粒子编号 */
const MOTES = Array.from({ length: 14 }, (_, i) => i);

/* ---------------- 视频背景 · 交叉溶解无缝循环 ----------------
   航拍前飞视频直接 loop 会有跳帧接缝：双 video 轮换，前一段快播完时
   另一段从头起播，两段在 CROSSFADE 秒内做缓动交叉溶解转场，循环处自然过渡。 */
const CROSSFADE = 2.5; // 交叉溶解时长（秒），与 CSS .aco30-flybg video 的 transition 保持一致。
                       // ⚠️取大值(而非1.3)有两个作用：①转场更慢更从容；②提前2.5s起播新段，
                       // 让"旧段中段画面"与"新段开头画面"是不同场景，溶解才看得见（无缝视频首尾同帧，
                       // 在循环点做溶解等于溶解两张相同画面，肉眼无感）。
const VideoLoopBg: React.FC<{ src: string; poster: string }> = ({ src, poster }) => {
  const vRefs = [useRef<HTMLVideoElement | null>(null), useRef<HTMLVideoElement | null>(null)];
  const frontRef = useRef<0 | 1>(0);
  const swapRef = useRef(false);
  const [front, setFront] = useState<0 | 1>(0);
  /* 每段透明度：新段在上层淡入(0→1)，旧段在下层保持不透明直到被盖住再归零 —— 中途不露垫图 */
  const [op, setOp] = useState<[number, number]>([1, 0]);

  const onTime = (which: 0 | 1) => () => {
    if (which !== frontRef.current || swapRef.current) return;
    const cur = vRefs[which].current;
    const next = (which === 0 ? 1 : 0) as 0 | 1;
    const nxt = vRefs[next].current;
    if (!cur || !nxt || !cur.duration) return;
    /* 剩余时间进入交叉溶解窗口 → 下一段从头起播并在上层淡入 */
    if (cur.duration - cur.currentTime <= CROSSFADE) {
      swapRef.current = true;
      try { nxt.currentTime = 0; } catch { /* ignore */ }
      nxt.play().catch(() => {});
      frontRef.current = next;
      setFront(next);
      /* 新段淡入到 1；旧段先保持 1（在下层顶着，防露垫图） */
      setOp(next === 0 ? [1, 1] : [1, 1]);
      /* 溶解结束：旧段已被完全盖住，停下、归零透明度、重置播放位置 */
      setTimeout(() => {
        try { cur.pause(); cur.currentTime = 0; } catch { /* ignore */ }
        setOp(next === 0 ? [1, 0] : [0, 1]);
        swapRef.current = false;
      }, CROSSFADE * 1000 + 120);
    }
  };

  return (
    <div className="aco30-flybg">
      <img className="pad" src={poster} alt="" draggable={false} />
      <video ref={vRefs[0]} src={src} muted playsInline autoPlay preload="auto"
        style={{ opacity: op[0], zIndex: front === 0 ? 2 : 1 }} onTimeUpdate={onTime(0)} />
      <video ref={vRefs[1]} src={src} muted playsInline preload="auto"
        style={{ opacity: op[1], zIndex: front === 1 ? 2 : 1 }} onTimeUpdate={onTime(1)} />
    </div>
  );
};

/** deptId → 所在页与槽位 */
const deptSlotIn = (pages: Record<DeptCategory, HallEntry[]>, deptId: string): { cat: DeptCategory; idx: number } | null => {
  for (const cat of ['internal', 'external'] as DeptCategory[]) {
    const idx = pages[cat].findIndex((e) => (e.kind !== 'expand' && e.deptId === deptId));
    if (idx >= 0) return { cat, idx };
  }
  return null;
};

const CommandHallMode: React.FC<HallModeProps> = ({
  scene, setScene, deptAgents, tasks, theater, runningByDept,
  selDeptId, selAgentId, pingDeptId, onChooseDept, onPickAgent, onOpenTask,
  focusDeptId, onExitFocus,
}) => {
  const sceneRef = useRef<DeptCategory>(scene);
  sceneRef.current = scene;
  const pages = HALL_PAGES;
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  /* 存储键升到 v3：所有人默认落到"航拍飞行"视频背景，之后手动选择照常记忆 */
  const [floorKey, setFloorKey] = useState(() => {
    const saved = localStorage.getItem('aco.hallfloor3');
    return saved && HALL_FLOORS.some((f) => f.key === saved) ? saved : 'fly';
  });
  const [floorMenu, setFloorMenu] = useState(false);
  useEffect(() => { localStorage.setItem('aco.hallfloor3', floorKey); }, [floorKey]);
  const floorConf = HALL_FLOORS.find((f) => f.key === floorKey);
  const floorImg = floorConf?.img || HALL_USER_BG;
  /** 飞行模式：视频背景下工作台化身悬浮飞行台（喷气+起伏+无落地影） */
  const flying = !!floorConf?.video;
  const prevStatusRef = useRef<Record<string, string>>({});
  const seenTaskRef = useRef<Set<string>>(new Set());
  const { canvasRef, fire } = useFxCanvas();
  const beamRef = useRef<Beam | null>(null);
  const beamCanvasRef = useBeamCanvas(beamRef);

  const selDept = selDeptId ? deptOf(selDeptId) : null;
  const agents = selDeptId ? (deptAgents[selDeptId] || []) : [];
  const zones: DeptCategory[] = ['internal', 'external'];

  /* 运镜聚焦：算出焦点槽位（仅当焦点部门在当前场景页） */
  const focusLoc = focusDeptId ? deptSlotIn(pages, focusDeptId) : null;
  const focused = !!(focusLoc && focusLoc.cat === scene);
  const focusSlot = focused ? HALL_SLOTS[focusLoc!.idx] : null;

  const platPoint = useCallback((deptId: string) => {
    const loc = deptSlotIn(pagesRef.current, deptId);
    if (!loc) return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    if (loc.cat !== sceneRef.current) {
      return loc.cat === 'external'
        ? { x: window.innerWidth - 60, y: window.innerHeight * 0.5 }
        : { x: 60, y: window.innerHeight * 0.5 };
    }
    const s = HALL_SLOTS[loc.idx];
    return { x: (s.x / 100) * window.innerWidth, y: ((s.y - 6) / 100) * window.innerHeight };
  }, []);

  /* 选中部门 → 工作台向中央舞台拉出流光线（运镜聚焦时收起） */
  useEffect(() => {
    if (!selDept || focused || deptSlotIn(pagesRef.current, selDept.id)?.cat !== scene) { beamRef.current = null; return; }
    const p = platPoint(selDept.id);
    beamRef.current = {
      x0: p.x, y0: p.y + window.innerHeight * 0.02,
      x1: window.innerWidth / 2, y1: window.innerHeight * 0.56,
      color: selDept.accent, born: performance.now(),
    };
  }, [selDept, scene, platPoint, focused]);

  /* 新任务 → 令牌飞抵工作台（会话恢复的历史任务不飞） */
  useEffect(() => {
    for (const t of tasks) {
      if (seenTaskRef.current.has(t.localId)) continue;
      seenTaskRef.current.add(t.localId);
      if (t.localId.startsWith('srv-')) continue;
      const d = deptOf(t.deptId);
      const p = platPoint(t.deptId);
      fire(window.innerWidth / 2, window.innerHeight - 140, p.x, p.y, d.accent, true);
    }
  }, [tasks, fire, platPoint]);

  /* 任务收尾 → 回执飞任务坞 */
  useEffect(() => {
    for (const t of tasks) {
      const prev = prevStatusRef.current[t.localId];
      if (prev && prev !== t.status && (t.status === 'completed' || t.status === 'failed')) {
        const d = deptOf(t.deptId);
        const p = platPoint(t.deptId);
        fire(p.x, p.y, window.innerWidth - 170, 120, t.status === 'completed' ? d.accent : '#f43f5e');
      }
      prevStatusRef.current[t.localId] = t.status;
    }
  }, [tasks, fire, platPoint]);

  return (
    <div className={`aco12-hall ${focused ? 'focused' : ''} ${flying ? 'fly' : ''}`}>
      {/* 背景：航拍飞行=视频无缝循环；其余=静态地面 */}
      {floorConf?.video
        ? <VideoLoopBg key={floorKey} src={floorConf.video} poster={floorImg} />
        : <img key={floorKey} className="aco12-bg aco10-fade" src={floorImg} alt="" draggable={false} />}

      {/* 地面切换器 */}
      <div className="aco15-floor" onClick={(e) => e.stopPropagation()}>
        <button className="cur" onClick={() => setFloorMenu((v) => !v)}>🖼 背景 ▾</button>
        {floorMenu && (
          <div className="menu">
            {HALL_FLOORS.map((f) => (
              <button key={f.key} className={`fi ${floorKey === f.key ? 'on' : ''}`} onClick={() => { setFloorKey(f.key); }}>
                <img src={f.img} alt="" draggable={false} />
                <em>{f.label}</em>
              </button>
            ))}
          </div>
        )}
      </div>
      <canvas ref={beamCanvasRef} className="aco3-ambient" />


      {/* 双页槽位层（业主/外协滑动切换；聚焦时对整页做丝滑运镜推进） */}
      {zones.map((cat) => (
        <section
          key={cat}
          className={`aco12-page ${scene === cat ? 'cur' : cat === 'external' ? 'off-r' : 'off-l'}`}
          style={scene === cat && focusSlot ? {
            /* 运镜=纯平移(用户定案不放大)：把该部门工作台丝滑移到画面中央 */
            transform: `translate(${50 - focusSlot.x}%, ${56 - focusSlot.y}%)`,
          } : undefined}
        >
          {pages[cat].map((entry, i) => (
            <HallSpot
              key={`${cat}-${i}`}
              entry={entry}
              slotIdx={i}
              slot={HALL_SLOTS[i]}
              selected={entry.kind !== 'expand' && entry.deptId === selDeptId}
              working={entry.kind !== 'expand' ? runningByDept[entry.deptId] : undefined}
              staged={entry.kind !== 'expand' ? theater.staged[entry.deptId] : undefined}
              pinging={entry.kind !== 'expand' && entry.deptId === pingDeptId}
              focusState={focused ? (entry.kind !== 'expand' && entry.deptId === focusDeptId ? 'target' : 'dim') : 'none'}
              onSelect={entry.kind !== 'expand' ? () => onChooseDept(entry.deptId) : undefined}
              onOpenTask={onOpenTask}
            />
          ))}
        </section>
      ))}

      {/* 聚焦模式：返回全景 */}
      {focused && (
        <button className="aco17-back" onClick={(e) => { e.stopPropagation(); onExitFocus?.(); }}>
          ‹ 返回全景
        </button>
      )}

      {/* 页切换门 */}
      {zones.map((cat) => {
        if (cat === scene) return null;
        const sc = SCENES[cat];
        const running = tasks.filter((t) => (t.status === 'running' || t.status === 'dispatching') && deptSlotIn(pages, t.deptId)?.cat === cat).length;
        const side = cat === 'external' ? 'right' : 'left';
        return (
          <button
            key={`portal-${cat}`}
            className={`aco3-portal p-${side}`}
            style={{ '--zt': sc.tint, '--zts': sc.tintSoft } as React.CSSProperties}
            onClick={(e) => { e.stopPropagation(); setScene(cat); }}
            title={`切换到${sc.label}`}
          >
            <span className="pt-arrow">{side === 'right' ? '›' : '‹'}</span>
            <span className="pt-glyph">{sc.glyph}</span>
            <span className="pt-label">{sc.label}</span>
            <span className="pt-sub">点击进入</span>
            {running > 0 && <span className="pt-badge">{running} 个任务进行中</span>}
          </button>
        );
      })}

      {/* 中央 · 智能体舞台（运镜聚焦时让位） */}
      {selDept && !focused && deptSlotIn(pages, selDept.id)?.cat === scene && (
        <AgentStage
          key={selDept.id}
          dept={selDept}
          agents={agents}
          selAgentId={selAgentId}
          working={runningByDept[selDept.id]}
          onPickAgent={onPickAgent}
          onOpenTask={onOpenTask}
        />
      )}

      <canvas ref={canvasRef} className="aco3-fx" />
    </div>
  );
};

/* ---------------- 单个槽位：工作台图 + 状态光效 + 名牌 + 气泡 ---------------- */
const HallSpot: React.FC<{
  entry: HallEntry;
  slotIdx: number;
  slot: { x: number; y: number; scale: number };
  selected: boolean;
  working?: OfficeTask;
  staged?: { task: OfficeTask; beat: Beat };
  pinging: boolean;
  /** 运镜聚焦状态：target=镜头主角 dim=淡出让位 */
  focusState?: 'none' | 'target' | 'dim';
  onSelect?: () => void;
  onOpenTask: (localId: string) => void;
}> = ({ entry, slotIdx, slot, selected, working, staged, pinging, focusState = 'none', onSelect, onOpenTask }) => {
  const dept: DeptConf | null = entry.kind !== 'expand' ? deptOf(entry.deptId) : null;
  const persona = dept ? PERSONAS[dept.id] : null;
  const beat: Beat = staged?.beat || 'idle';
  const busy = ['receive', 'assign', 'working', 'submit', 'review'].includes(beat);
  const state = entry.kind === 'expand' ? 'off' : selected ? 'active' : busy ? 'work' : 'idle';
  const bubble = useMemo(
    () => (staged && dept ? beatBubble(staged.task, dept.id, staged.beat) : null),
    [staged, dept],
  );
  const gem = (beat === 'apologize' || beat === 'failed') ? 'red' : busy ? 'amber' : 'green';
  /* 名牌位置随台子朝向：左列=屏幕左后上方，中间=正上方，右列=屏幕右后上方 */
  const plateSide = slotIdx <= 1 ? 'l' : slotIdx === 2 ? 'c' : 'r';

  return (
    <div
      className={`aco12-spot k-${entry.kind} st-${state} fs-${focusState} ${pinging ? 'ping' : ''}`}
      style={{
        left: `${slot.x}%`, top: `${slot.y}%`,
        width: `calc(min(38vh, 19vw) * ${slot.scale})`,
        '--acc': dept?.accent || '#9aa7bd', '--accSoft': dept?.accentSoft || 'rgba(154,167,189,.3)',
        '--si': String(slotIdx),
      } as React.CSSProperties}
      onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(); } : undefined}
      role={onSelect ? 'button' : undefined}
    >
      {/* 选中：天顶聚光（seedance 生成的体积光帧动画，screen 混合免抠图）+ 底座旋转光环 + 头顶落标 */}
      {state === 'active' && (
        <>
          <span className="aco17-beam"><i /></span>
          <span className="aco12-spinring"><i /></span>
          <span className="aco12-pin" />
        </>
      )}
      {state === 'work' && <span className="aco12-spinring slow"><i /></span>}
      {/* 运镜聚焦主角：粒子能量光环帧动画环绕 + 办理横幅 */}
      {focusState === 'target' && (
        <>
          <span className="aco17-fxring"><i /></span>
          <span className="aco17-workchip">部门已接令 · 正在为您办理</span>
        </>
      )}
      {/* 落地接触投影（飞行模式下隐藏） */}
      <span className="aco12-shadow" />
      {/* 飞行模式：整幅底座喷气幕（seedance 帧动画真透明）。
          只从底座下沿开始显示（wrap 裁剪），天然被工作台遮挡，只见喷出的部分 */}
      {entry.kind !== 'expand' && <span className="aco16-jetwrap" aria-hidden><i /></span>}
      {/* 常态动效：底座双圈环绕光粒 + 台面上方上浮光尘 */}
      {entry.kind !== 'expand' && (
        <>
          <span className="aco12-orbit">
            <i /><i className="p2" /><i className="p3" /><i className="p4" /><i className="p5" /><i className="p6" />
          </span>
          <span className="aco12-orbit hi"><i /><i className="p2" /><i className="p3" /></span>
          <span className="aco16-motes">
            {MOTES.map((k) => <i key={k} style={{ '--mi': String(k) } as React.CSSProperties} />)}
          </span>
        </>
      )}
      <span className={`flip ${entry.mirror ? 'on' : ''}`}>
        {/* tblbob=晃动专属包裹层：ping/st-work 会抢占 img 的 animation，晃动必须放在没人碰的独立层 */}
        <span className="tblbob">
          <img className="aco12-tbl" src={entry.img} alt={dept?.name || '待扩展'} draggable={false} />
        </span>
      </span>
      {/* 关闭台：静态"待接入"徽标（呼吸电源图标被否，静态、克制） */}
      {entry.kind === 'expand' && <span className="aco16-standby" aria-hidden><i /></span>}

      {/* 台词气泡 */}
      {bubble && dept && (
        <div className="aco12-say">
          <SpeechBubble
            key={`${staged?.task.localId}-${staged?.beat}`}
            text={bubble.text}
            accent={dept.accent}
            tone={staged?.beat === 'apologize' ? 'bad' : staged?.beat === 'deliver' || staged?.beat === 'done' ? 'ok' : 'normal'}
          />
          {(staged?.beat === 'deliver' || staged?.beat === 'done') && staged && (
            <button className="aco8-bubble-act" onClick={(e) => { e.stopPropagation(); onOpenTask(staged.task.localId); }}>
              查看成果
            </button>
          )}
        </div>
      )}

      {/* 部门职责：贴在名牌正上方、随名牌左/中/右方位对齐，层级高于名牌，绝不重叠 */}
      {state === 'active' && dept?.duties && dept.duties.length > 0 && (
        <div className={`aco35-duties side-${plateSide}`}>
          {dept.duties.map((d) => <i key={d}>{d}</i>)}
        </div>
      )}

      {/* 名牌：立在屏幕上方，透视角度贴合台子朝向 */}
      <div
        className={`aco14-plate side-${plateSide} ${entry.kind === 'expand' ? 'off' : ''}`}
        onClick={onSelect ? (e) => { e.stopPropagation(); onSelect!(); } : undefined}
      >
        {entry.kind === 'expand' ? (
          <>
            <i className="plus"><PlusOutlined /></i>
            <b>{entry.label || '待扩展'}</b>
            <em>{entry.label ? '待接入' : '可接入新部门'}</em>
          </>
        ) : (
          <>
            <span className={`aco16-lamp s-${gem}`}><i className="halo" /><i className="core" /><i className="arc" /></span>
            <b>{dept!.name}</b>
            <em>
              {entry.kind === 'pending' ? `${persona?.manager} · 素材制作中` : `${persona?.manager} · ${
                busy ? '办理中' : beat === 'apologize' || beat === 'failed' ? '异常' : beat === 'done' ? '已呈报' : '在岗'
              }`}
            </em>
          </>
        )}
        {/* 进度牌以部门名牌为定位基准，固定显示在框体右侧，随名牌一起移动且永不重叠。 */}
        {working && !bubble && (
          <div className="aco3-taskchip">
            <LoadingOutlined className="sp" />
            <div className="tx">
              <b>{working.agentName}</b>
              <span>{working.stepText}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- 中央 · 智能体舞台 ---------------- */
const AgentStage: React.FC<{
  dept: DeptConf;
  agents: OfficeAgent[];
  selAgentId: string | null;
  working?: OfficeTask;
  onPickAgent: (a: OfficeAgent) => void;
  onOpenTask: (id: string) => void;
}> = ({ dept, agents, selAgentId, working, onPickAgent, onOpenTask }) => {
  return (
    <div className="aco3-stage" style={{ '--acc': dept.accent, '--accSoft': dept.accentSoft } as React.CSSProperties} onClick={(e) => e.stopPropagation()}>
      <div className="st-cap">
        <b>{dept.name}</b>
        <span>{SCENES[dept.category].glyph} {SCENES[dept.category].label} · 选择智能体或直接下指令</span>
      </div>
      <div className="st-agents">
        {agents.map((a, i) => (
          <button
            key={a.id}
            className={`st-agent ${selAgentId === a.id ? 'on' : ''} ${!a.available ? 'dis' : ''}`}
            style={{ animationDelay: `${0.42 + i * 0.11}s` } as React.CSSProperties}
            disabled={!a.available}
            title={a.desc}
            onClick={() => onPickAgent(a)}
          >
            <span className="av">
              <img src={AGENT_ICONS[a.kind]} alt="" draggable={false} />
              {!a.available && <LockOutlined className="lk" />}
              {a.kind === 'devteam' && <RocketOutlined className="rk" />}
            </span>
            <b>{a.name}</b>
            <em>{a.kind === 'general' ? '文档·PPT·表格·设计图' : a.kind === 'devteam' ? '软件开发' : a.available ? '业务在线' : '待接入'}</em>
          </button>
        ))}
      </div>
      {working && (
        <button className="st-task" onClick={() => onOpenTask(working.localId)}>
          <LoadingOutlined /> <b>{working.agentName}</b> {working.stepText}
        </button>
      )}
    </div>
  );
};

export default CommandHallMode;
